/**
 * Web Crypto API encryption service for KYP Backend
 * Matches Java EncryptionUtil exactly: AES/GCM/NoPadding with 12-byte IV and 16-byte tag
 */

interface EncryptionConfig {
  name: "AES-GCM";
  iv: Uint8Array;
  tagLength: number;
}

type EncryptTableData = string | object | null | undefined;
type EncryptedResult = string | null | undefined;

class EncryptionService {
  private readonly encryptionEnabled: boolean;
  private readonly encryptionKey: string;
  private key: CryptoKey | null;

  constructor() {
    this.encryptionEnabled = true; // Force enable since backend is sending encrypted data
    this.encryptionKey = process.env.REACT_APP_KYP_SERVICE_256_SECRET_KEY || "";
    this.key = null;
    this.initializeKey();
  }

  /**
   * Initialize the encryption key using Web Crypto API
   */
  private async initializeKey(): Promise<void> {
    try {
      let keyData: ArrayBuffer;

      // Check if key is base64 encoded (like Java backend)
      if (this.encryptionKey && this.encryptionKey.length > 16) {
        try {
          // Try to decode as base64 first
          keyData = this.base64ToArrayBuffer(this.encryptionKey);
        } catch (e) {
          // If base64 decode fails, use as string
          const paddedKey: string = this.encryptionKey
            .padEnd(32, "0")
            .substring(0, 32);
          keyData = new TextEncoder().encode(paddedKey).buffer;
        }
      } else {
        // Use string key directly, pad to 32 bytes for AES-256
        const paddedKey: string = this.encryptionKey
          .padEnd(32, "0")
          .substring(0, 32);
        keyData = new TextEncoder().encode(paddedKey).buffer;
      }

      // Import key for AES-GCM
      this.key = await crypto.subtle.importKey(
        "raw",
        keyData,
        { name: "AES-GCM" },
        false,
        ["encrypt", "decrypt"]
      );

      console.log("Web Crypto API key initialized successfully");
    } catch (error) {
      console.error("Failed to initialize encryption key:", error);
      this.key = null;
    }
  }

  /**
   * Convert base64 string to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString: string = atob(base64);
    const bytes: Uint8Array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer as ArrayBuffer;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes: Uint8Array = new Uint8Array(buffer);
    let binary: string = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Encrypt data using AES-GCM (matches Java backend exactly)
   */
  public async encrypt(data: EncryptTableData): Promise<EncryptedResult> {
    if (!this.encryptionEnabled || !data) {
      return data as string | null | undefined;
    }

    if (!this.key) {
      await this.initializeKey();
      if (!this.key) {
        throw new Error("Encryption key not initialized");
      }
    }

    try {
      const jsonString: string =
        typeof data === "string" ? data : JSON.stringify(data);
      const plaintext = new TextEncoder().encode(jsonString);

      // Generate 12-byte IV for GCM (matches Java GCM_IV_LENGTH = 12)
      const iv: Uint8Array = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt using AES-GCM
      const encrypted: ArrayBuffer = await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: iv,
          tagLength: 128, // 16 bytes = 128 bits
        } as EncryptionConfig,
        this.key,
        plaintext
      );

      // Combine IV + encrypted data (matches Java format)
      const combined: Uint8Array = new Uint8Array(
        iv.length + encrypted.byteLength
      );
      combined.set(iv, 0);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Return as base64
      return this.arrayBufferToBase64(combined.buffer as ArrayBuffer);
    } catch (error) {
      console.error("Encryption failed:", error);
      throw new Error(`Failed to encrypt data: ${(error as Error).message}`);
    }
  }

  /**
   * Decrypt data using AES-GCM (matches Java backend exactly)
   */

  public async decrypt(
    encryptedData: EncryptedResult
  ): Promise<EncryptTableData> {
    if (!this.encryptionEnabled || !encryptedData) {
      return encryptedData;
    }

    if (!this.key) {
      await this.initializeKey();
      if (!this.key) {
        throw new Error("Encryption key not initialized");
      }
    }

    try {
      // Handle URL encoding
      let decodedData: string = decodeURIComponent(encryptedData);

      // Handle URL-safe base64
      decodedData = decodedData.replace(/-/g, "+").replace(/_/g, "/");
      while (decodedData.length % 4) {
        decodedData += "=";
      }

      // Decode base64 to ArrayBuffer
      const encryptedWithIv: ArrayBuffer =
        this.base64ToArrayBuffer(decodedData);

      // Extract IV (first 12 bytes) and encrypted data
      const iv: Uint8Array = new Uint8Array(encryptedWithIv.slice(0, 12));
      const ciphertext: ArrayBuffer = encryptedWithIv.slice(12);

      // Decrypt using AES-GCM
      const decrypted: ArrayBuffer = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: iv,
          tagLength: 128,
        } as EncryptionConfig,
        this.key,
        ciphertext
      );

      // Convert back to string
      let jsonString = new TextDecoder("utf-8").decode(decrypted);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("Decryption failed:", error);
      throw new Error(`Failed to decrypt data: ${(error as Error).message}`);
    }
  }

  /**
   * Check if encryption is enabled
   */
  public isEncryptionEnabled(): boolean {
    return this.encryptionEnabled;
  }

  /**
   * Check if the encryption service is ready to use
   */
  public isReady(): boolean {
    return this.key !== null;
  }
}

export default new EncryptionService();
